import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { enUS, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { DayPicker, useDayPicker, useNavigation, type CaptionProps } from "react-day-picker";
import { format } from "date-fns";

import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const navButtonClassName =
  "inline-flex h-6 w-6 items-center justify-center rounded-md p-0 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-30";
const dayButtonClassName =
  "inline-flex h-9 w-9 items-center justify-center !rounded-full p-0 text-sm font-normal text-foreground transition-colors hover:[background:var(--bg-brand-soft)] hover:[color:var(--fg-brand)] aria-selected:opacity-100";

function CalendarCaption({ displayMonth }: CaptionProps) {
  const { previousMonth, nextMonth, goToMonth } = useNavigation();
  const { locale } = useDayPicker();

  return (
    <div className="flex items-center justify-center gap-1 pt-1">
      <button
        type="button"
        className={navButtonClassName}
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        aria-label="Previous month"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="text-sm font-semibold text-color-dark capitalize">
        {format(displayMonth, "LLLL yyyy", { locale })}
      </span>
      <button
        type="button"
        className={navButtonClassName}
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        aria-label="Next month"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function Calendar({ className, classNames, showOutsideDays = true, locale, ...props }: CalendarProps) {
  const { i18n } = useTranslation();
  const resolvedLocale = locale ?? (i18n.language.startsWith("en") ? enUS : ptBR);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={resolvedLocale}
      className={cn( className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-semibold text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
        day: dayButtonClassName,
        day_range_end: "",
        day_selected:
          "[background:var(--fg-brand)] [color:var(--bg-surface)] hover:[background:var(--fg-brand)] hover:[color:var(--bg-surface)] focus:[background:var(--fg-brand)] focus:[color:var(--bg-surface)]",
        day_today: "bg-transparent rounded-full border font-semibold text-color-dark [border-color:var(--border-strong)] aria-selected:![background:var(--fg-brand)] aria-selected:![color:var(--bg-surface)] aria-selected:!border-transparent",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:opacity-100 aria-selected:[color:var(--bg-surface)]",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        Caption: CalendarCaption,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
